import { computed, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { BdProgressCounts } from '@bindoc-projects/ui-lib/feedback/ui';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  from,
  map,
  Observable,
  of,
  take,
} from 'rxjs';

const IDLE: BdProgressCounts = {
  state: 'idle',
  completed: 0,
  total: 0,
  failed: 0,
};

/**
 * A bulk shareable operation: one request per item, one after the other. A
 * failed item counts and the batch goes on, so the indicator can show the
 * failed share next to the successful one.
 */
export class ShareableBatchRun {
  private readonly _counts = signal<BdProgressCounts>(IDLE);

  public readonly counts = this._counts.asReadonly();
  public readonly state = computed(() => this._counts().state);
  public readonly completed = computed(() => this._counts().completed);
  public readonly total = computed(() => this._counts().total);
  public readonly failed = computed(() => this._counts().failed);

  constructor(private readonly _destroyRef: DestroyRef) {}

  public run<T>(
    items: readonly T[],
    attempt: (item: T) => Observable<unknown>,
  ): void {
    this._counts.set({ ...IDLE, state: 'running', total: items.length });
    from(items)
      .pipe(
        concatMap((item) =>
          attempt(item).pipe(
            take(1),
            map(() => true),
            defaultIfEmpty(true),
            catchError(() => of(false)),
          ),
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (succeeded) =>
          this._counts.update((counts) => ({
            ...counts,
            completed: counts.completed + 1,
            failed: succeeded ? counts.failed : counts.failed + 1,
          })),
        complete: () =>
          this._counts.update((counts) => ({ ...counts, state: 'done' })),
      });
  }
}
